/**
 * Should a hand-rolled "close on outside pointerdown" container
 * dismiss itself for a pointerdown that landed on `target`?
 *
 * Returns FALSE (keep the container open) when the target is:
 *   1. inside the container's own DOM subtree, OR
 *   2. inside ANY Radix-portaled floating layer
 *      (Popover / DropdownMenu / Select / Combobox / Tooltip / …).
 *
 * Radix renders floating content into a portal on `document.body`
 * wrapped in `[data-radix-popper-content-wrapper]`, which lives
 * OUTSIDE the triggering container's subtree. A naive
 * `container.contains(target)` therefore treats a click inside
 * such an overlay as "outside" and closes the container.
 *
 * Concrete bug this guards (2026-05-31): the missions bulk-bar
 * Assign panel closed the instant an operator clicked a spatialist
 * in the (portaled) `<MissionAssigneePicker>` — the bar's manual
 * `document` pointerdown listener saw the portaled click as
 * outside and dismissed the panel BEFORE the pick's onChange ran,
 * so the assignment silently never happened. Same latent trap for
 * the `<SmartDateTimePicker>` calendar in the Schedule panel and
 * any future Radix overlay opened from within the bar.
 */
/**
 * Portaled overlays that live outside a modal Dialog's DOM (Google
 * Places `.pac-container`, Radix poppers, program-team / assignee
 * pickers). Dialog dismiss/focus-outside must ignore these so nested
 * search and add fields can keep focus.
 */
export const NESTED_DIALOG_OVERLAY_SELECTOR = [
  ".pac-container",
  "[data-radix-popper-content-wrapper]",
  "[data-vs-assignee-picker-content]",
  "[data-vs-program-team-picker-content]",
].join(", ");

export function isNestedDialogOverlayTarget(
  target: EventTarget | null,
): boolean {
  const el = target as Element | null;
  if (!el || typeof el.closest !== "function") return false;
  return Boolean(el.closest(NESTED_DIALOG_OVERLAY_SELECTOR));
}

export function shouldDismissOnOutsidePointerDown(
  target: EventTarget | null,
  container: Element | null,
): boolean {
  if (!container) return false;
  const el = target as Element | null;
  // Non-element targets (document, window) count as outside.
  if (!el || typeof el.closest !== "function") return true;
  // Inside the container's own UI — never dismiss.
  if (container.contains(el)) return false;
  // A Radix-portaled overlay opened from within the container is
  // conceptually part of the container's UI even though it lives
  // in a body-level portal. Keep the container open.
  if (isNestedDialogOverlayTarget(el)) return false;
  return true;
}
